import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import { Building2, Upload, FileText, Check, Loader2, ArrowLeft, ArrowRight, Plus, Trash2, Users, Globe } from 'lucide-react';
import { FOREIGN_COUNTRIES, ENTITY_TYPES, JURISDICTIONS, JURISDICTION_LABEL } from '@/lib/foreignCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const COUNTRIES = [{ code: 'US', name: 'United States' }, ...FOREIGN_COUNTRIES];

const OWNER_ROLES = ['Beneficial Owner', 'Shareholder', 'Director', 'Member', 'Partner', 'Nominee Director'];

const US_ENTITY_TYPES = [
  { value: 'us_llc', label: 'US LLC' },
  { value: 'us_c_corp', label: 'US C-Corp' },
];

const entityOptionsFor = (country) => {
  if (country === 'US') return US_ENTITY_TYPES;
  return (ENTITY_TYPES[country] || []).map(e => ({ value: e, label: e }));
};

const jurisdictionOptionsFor = (country) => {
  if (country === 'US') return US_STATES;
  return JURISDICTIONS[country] || [];
};

const jurisdictionLabelFor = (country) => {
  if (country === 'US') return 'State of Formation';
  return JURISDICTION_LABEL[country] || 'Jurisdiction';
};

const emptyOwner = () => ({ owner_type: 'individual', full_name: '', ownership_percentage: '', roles: [], nationality: '', id_number: '', doc: null, uploading: false, uploadError: '' });

const STEPS = [
  { n: 1, label: 'Details', icon: Building2 },
  { n: 2, label: 'Owners', icon: Users },
  { n: 3, label: 'Documents', icon: FileText },
];

const SelfRegisterPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState(null);

  // Step 1
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('US');
  const [entityType, setEntityType] = useState('us_llc');
  const [jurisdiction, setJurisdiction] = useState('Delaware');
  const [dateOfFormation, setDateOfFormation] = useState('');
  const [ein, setEin] = useState('');

  // Step 2
  const [owners, setOwners] = useState([emptyOwner()]);

  // Step 3
  const [docs, setDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const inputCls = 'w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]';

  const handleCountry = (c) => {
    setCountry(c);
    const ents = entityOptionsFor(c);
    setEntityType(ents[0]?.value || '');
    const j = jurisdictionOptionsFor(c);
    setJurisdiction(j.length === 1 ? j[0] : (c === 'US' ? 'Delaware' : ''));
  };

  const updateOwner = (i, field, val) => setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, [field]: val } : o));
  const toggleOwnerRole = (i, role) => setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, roles: o.roles.includes(role) ? o.roles.filter(r => r !== role) : [...o.roles, role] } : o));
  const removeOwner = (i) => setOwners(prev => prev.filter((_, idx) => idx !== i));

  const uploadOwnerDoc = async (i, file) => {
    if (!file) return;
    setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, uploading: true, uploadError: '' } : o));
    try {
      const pres = await axios.post(`${API}/upload/presigned`, { file_name: file.name, content_type: file.type || 'application/octet-stream' }, authHeader);
      const { upload_url, file_url, s3_key } = pres.data;
      await axios.put(upload_url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, uploading: false, doc: { file_name: file.name, file_url, s3_key } } : o));
    } catch (e) {
      setOwners(prev => prev.map((o, idx) => idx === i ? { ...o, uploading: false, uploadError: 'Upload failed. Please try again.' } : o));
    }
  };

  // Validate + create company shell on entering the documents step
  const goToDocuments = async () => {
    setError('');
    for (let i = 0; i < owners.length; i++) {
      const o = owners[i];
      if (!o.full_name.trim() || !o.ownership_percentage || !o.nationality.trim() || !o.id_number.trim()) {
        setError(`Complete all fields for Owner ${i + 1}.`); return;
      }
      if (!o.roles || o.roles.length === 0) { setError(`Select at least one role for Owner ${i + 1}.`); return; }
      if (!o.doc) { setError(`Upload the ${o.owner_type === 'individual' ? 'passport' : 'formation certificate'} for Owner ${i + 1}.`); return; }
    }
    if (companyId) { setStep(3); return; }
    setSaving(true);
    try {
      const payload = {
        company_name: companyName.trim(),
        country,
        entity_type: entityType,
        state_of_formation: jurisdiction,
        date_of_formation: dateOfFormation || null,
        ein: ein.trim() || null,
        owners: owners.map(o => ({
          owner_type: o.owner_type,
          full_name: o.full_name.trim(),
          ownership_percentage: o.ownership_percentage ? parseFloat(o.ownership_percentage) : null,
          role: o.roles[0] || null,
          roles: o.roles,
          id_type: o.owner_type === 'individual' ? 'passport' : 'company_registration',
          id_number: o.id_number.trim() || null,
          nationality: o.nationality.trim() || null,
          document_type: o.owner_type === 'individual' ? 'passport' : 'formation_certificate',
          document_file_name: o.doc?.file_name || null,
          document_url: o.doc?.file_url || null,
          document_s3_key: o.doc?.s3_key || null,
        })),
      };
      const res = await axios.post(`${API}/companies/existing`, payload, authHeader);
      setCompanyId(res.data.company_id);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create company. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    setError('');
    if (step === 1) {
      if (!companyName.trim()) { setError('Company name is required.'); return; }
      if (!entityType) { setError('Select an entity type.'); return; }
      if (!jurisdiction) { setError(`Select a ${jurisdictionLabelFor(country).toLowerCase()}.`); return; }
      setStep(2); return;
    }
    if (step === 2) { goToDocuments(); return; }
  };
  const back = () => { setError(''); setStep(s => Math.max(s - 1, 1)); };

  const handleUploadDoc = async (file) => {
    if (!file || !companyId) return;
    if (file.size > 10 * 1024 * 1024) { setError('File too large (max 10MB)'); return; }
    setUploadingDoc(true);
    setError('');
    try {
      const pres = await axios.post(`${API}/upload/presigned`, { file_name: file.name, content_type: file.type || 'application/pdf' }, authHeader);
      const { upload_url, file_url, s3_key } = pres.data;
      await axios.put(upload_url, file, { headers: { 'Content-Type': file.type || 'application/pdf' } });
      const docType = `existing_doc_${Date.now()}`;
      await axios.post(`${API}/companies/${companyId}/documents`, {
        document_category: 'formation',
        document_type: docType,
        file_name: file.name,
        file_url,
        s3_key,
        file_size: file.size,
      }, authHeader);
      setDocs(prev => [...prev, { document_type: docType, file_name: file.name }]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadingDoc(false);
    }
  };

  const submitForReview = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(`${API}/companies/${companyId}/submit-for-review`, {}, authHeader);
      if (res.data.submitted === false) {
        setError('Please upload at least one document to submit.');
      } else {
        navigate(`/dashboard/companies/${companyId}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <button onClick={() => navigate('/dashboard/companies')} className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors" data-testid="self-register-back">
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </button>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Add an Existing Company</h1>
        <p className="text-gray-400 text-sm mt-1">Already registered a company in the US, UK, Canada, Singapore or the UAE? Add it here and we'll activate it after a quick review.</p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="existing-steps">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = step === s.n;
          const done = step > s.n;
          return (
            <React.Fragment key={s.n}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap ${active ? 'bg-[#059669]/15 text-[#059669]' : done ? 'text-[#059669]' : 'text-gray-500'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${active ? 'bg-[#059669] text-black' : done ? 'bg-[#059669]/30' : 'bg-gray-800'}`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                <span className="text-sm font-medium">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-gray-700" />}
            </React.Fragment>
          );
        })}
      </div>

      {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6" data-testid="self-register-error"><p className="text-red-400 text-sm">{error}</p></div>}

      <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 max-w-3xl">
        {/* Step 1: Details */}
        {step === 1 && (
          <div className="space-y-5" data-testid="step1-form">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Name *</label>
              <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className={inputCls} placeholder="e.g. Falcon Trading Ltd" data-testid="step1-company-name" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Country *</label>
              <select value={country} onChange={e => handleCountry(e.target.value)} className={inputCls} data-testid="step1-country">
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Entity Type *</label>
                <select value={entityType} onChange={e => setEntityType(e.target.value)} className={inputCls} data-testid="step1-entity-type">
                  {entityOptionsFor(country).map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">{jurisdictionLabelFor(country)} *</label>
                <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} className={inputCls} data-testid="step1-jurisdiction">
                  <option value="">Select {jurisdictionLabelFor(country).toLowerCase()}</option>
                  {jurisdictionOptionsFor(country).map(j => <option key={j} value={j}>{j}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">{country === 'US' ? 'EIN' : 'Federal Tax ID'} <span className="text-gray-600">(optional)</span></label>
                <input type="text" value={ein} onChange={e => setEin(e.target.value)} className={inputCls} placeholder="If already assigned" data-testid="step1-ein" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Date of Formation <span className="text-gray-600">(optional)</span></label>
                <input type="date" value={dateOfFormation} onChange={e => setDateOfFormation(e.target.value)} className={inputCls} data-testid="step1-formation-date" />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Owners */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Add every owner, director and beneficial owner. All fields are mandatory. Individuals upload a passport; corporate owners upload a formation certificate.</p>
            {owners.map((o, i) => {
              const isCompany = o.owner_type === 'company';
              return (
                <div key={i} className="border border-gray-700 rounded-lg p-4" data-testid={`owner-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400 text-sm">Owner {i + 1}</span>
                    {owners.length > 1 && <button onClick={() => removeOwner(i)} className="text-red-400 hover:text-red-300" data-testid={`owner-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <div className="flex gap-2 mb-4">
                    {['individual', 'company'].map(t => (
                      <button key={t} onClick={() => updateOwner(i, 'owner_type', t)}
                        className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all ${o.owner_type === t ? 'border-[#059669] bg-[#059669]/10 text-[#059669]' : 'border-gray-700 text-gray-300 hover:border-gray-600'}`}
                        data-testid={`owner-type-${t}-${i}`}>
                        {t === 'individual' ? 'Individual' : 'Owned by a Company'}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="text" value={o.full_name} onChange={e => updateOwner(i, 'full_name', e.target.value)} placeholder={isCompany ? 'Company / entity name *' : 'Full name *'} className={inputCls} data-testid={`owner-name-${i}`} />
                    <input type="number" value={o.ownership_percentage} onChange={e => updateOwner(i, 'ownership_percentage', e.target.value)} placeholder="Ownership % *" className={inputCls} data-testid={`owner-pct-${i}`} />
                    <input type="text" value={o.nationality} onChange={e => updateOwner(i, 'nationality', e.target.value)} placeholder={isCompany ? 'Country of incorporation *' : 'Nationality *'} className={inputCls} data-testid={`owner-nationality-${i}`} />
                    <input type="text" value={o.id_number} onChange={e => updateOwner(i, 'id_number', e.target.value)} placeholder={isCompany ? 'Company registration no. *' : 'Passport / ID number *'} className={inputCls} data-testid={`owner-idnum-${i}`} />
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm text-gray-400 mb-2">Role(s) <span className="text-red-400">*</span> <span className="text-gray-600 text-xs">— select all that apply</span></label>
                    <div className="flex flex-wrap gap-2">
                      {OWNER_ROLES.map(r => {
                        const on = o.roles.includes(r);
                        return (
                          <button key={r} type="button" onClick={() => toggleOwnerRole(i, r)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${on ? 'border-[#059669] bg-[#059669]/15 text-[#059669]' : 'border-gray-700 text-gray-300 hover:border-gray-600'}`}
                            data-testid={`owner-role-${r.replace(/\s+/g, '-').toLowerCase()}-${i}`}>
                            {on && <Check className="w-3 h-3 inline mr-1" />}{r}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm text-gray-400 mb-2">{isCompany ? 'Formation Certificate' : 'Passport'} <span className="text-red-400">*</span></label>
                    {o.doc ? (
                      <div className="flex items-center justify-between bg-[#059669]/10 border border-[#059669]/30 rounded-lg px-3 py-2">
                        <span className="text-[#059669] text-sm truncate flex items-center gap-2"><Check className="w-4 h-4" /> {o.doc.file_name}</span>
                        <button onClick={() => updateOwner(i, 'doc', null)} className="text-gray-400 hover:text-red-400 text-xs">Replace</button>
                      </div>
                    ) : (
                      <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg py-3 cursor-pointer transition-colors ${o.uploading ? 'border-gray-700 text-gray-500' : 'border-gray-700 hover:border-[#059669] text-gray-300'}`} data-testid={`owner-upload-label-${i}`}>
                        {o.uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload {isCompany ? 'formation certificate' : 'passport'}</>}
                        <input type="file" accept="image/*,application/pdf" className="hidden" disabled={o.uploading} onChange={e => uploadOwnerDoc(i, e.target.files[0])} data-testid={`owner-upload-${i}`} />
                      </label>
                    )}
                    {o.uploadError && <p className="text-red-400 text-xs mt-1">{o.uploadError}</p>}
                  </div>
                </div>
              );
            })}
            <button onClick={() => setOwners([...owners, emptyOwner()])} className="inline-flex items-center gap-2 text-[#059669] hover:text-[#00D46E] text-sm" data-testid="add-owner-btn">
              <Plus className="w-4 h-4" /> Add another owner
            </button>
          </div>
        )}

        {/* Step 3: Documents */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step3-docs-section">
            <div>
              <h3 className="text-white font-semibold mb-1">Company Documents</h3>
              <p className="text-gray-400 text-sm">Upload your formation / registration documents. At least one document is required — add as many more as you like.</p>
            </div>

            {docs.length > 0 && (
              <div className="space-y-2">
                {docs.map((d, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#0b1310] border border-[#059669]/30 rounded-lg" data-testid={`uploaded-doc-${i}`}>
                    <Check className="w-5 h-5 text-[#059669]" />
                    <span className="text-white text-sm truncate">{d.file_name}</span>
                  </div>
                ))}
              </div>
            )}

            <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg py-4 cursor-pointer transition-colors ${uploadingDoc ? 'border-gray-700 text-gray-500' : 'border-gray-700 hover:border-[#059669] text-gray-300'}`} data-testid="add-doc-label">
              {uploadingDoc ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Plus className="w-4 h-4" /> {docs.length === 0 ? 'Upload a document' : 'Add another document'}</>}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={uploadingDoc} onChange={e => handleUploadDoc(e.target.files?.[0])} data-testid="add-doc-input" />
            </label>
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-8">
          <button onClick={back} disabled={step === 1 || saving} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-40" data-testid="existing-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 ? (
            <button onClick={next} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#059669' }} data-testid="existing-next-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <>Next <ArrowRight className="w-4 h-4" /></>}
            </button>
          ) : (
            <button onClick={submitForReview} disabled={saving || docs.length < 1} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: '#059669' }} data-testid="submit-for-review-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Check className="w-4 h-4" /> Submit for Review</>}
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default SelfRegisterPage;
